import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Globe, Mail, Phone, MapPin, RefreshCw, CheckCircle, XCircle, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FranchiseInquiryManager = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchInquiries();
  }, [filter]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const response = await axios.get(`${API}/admin/franchise-inquiries${params}`, {
        withCredentials: true
      });
      setInquiries(response.data.inquiries || []);
    } catch (error) {
      console.error("Error fetching inquiries:", error);
      toast.error("Failed to load franchise inquiries");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/franchise-inquiries/${inquiryId}/status?status=${newStatus}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchInquiries();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "new":
        return <Clock className="w-5 h-5 text-blue-500" />;
      case "contacted":
        return <Mail className="w-5 h-5 text-purple-500" />;
      case "qualified":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "approved":
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case "rejected":
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const stats = {
    total: inquiries.length,
    new: inquiries.filter(i => i.status === "new").length,
    contacted: inquiries.filter(i => i.status === "contacted").length,
    qualified: inquiries.filter(i => i.status === "qualified").length,
    approved: inquiries.filter(i => i.status === "approved").length
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Franchise Inquiries</h2>
        <Button onClick={fetchInquiries} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="text-sm text-gray-600">Total</div>
          <div className="text-2xl font-bold">{stats.total}</div>
        </Card>
        <Card className="p-4 border-blue-200">
          <div className="text-sm text-gray-600">New</div>
          <div className="text-2xl font-bold text-blue-600">{stats.new}</div>
        </Card>
        <Card className="p-4 border-purple-200">
          <div className="text-sm text-gray-600">Contacted</div>
          <div className="text-2xl font-bold text-purple-600">{stats.contacted}</div>
        </Card>
        <Card className="p-4 border-green-200">
          <div className="text-sm text-gray-600">Qualified</div>
          <div className="text-2xl font-bold text-green-600">{stats.qualified}</div>
        </Card>
        <Card className="p-4 border-green-300">
          <div className="text-sm text-gray-600">Approved</div>
          <div className="text-2xl font-bold text-green-700">{stats.approved}</div>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex space-x-2">
        <Button
          size="sm"
          variant={filter === "all" ? "default" : "outline"}
          onClick={() => setFilter("all")}
        >
          All
        </Button>
        <Button
          size="sm"
          variant={filter === "new" ? "default" : "outline"}
          onClick={() => setFilter("new")}
        >
          New
        </Button>
        <Button
          size="sm"
          variant={filter === "contacted" ? "default" : "outline"}
          onClick={() => setFilter("contacted")}
        >
          Contacted
        </Button>
        <Button
          size="sm"
          variant={filter === "qualified" ? "default" : "outline"}
          onClick={() => setFilter("qualified")}
        >
          Qualified
        </Button>
        <Button
          size="sm"
          variant={filter === "approved" ? "default" : "outline"}
          onClick={() => setFilter("approved")}
        >
          Approved
        </Button>
      </div>

      {/* Inquiries List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {inquiries.length === 0 ? (
            <Card className="p-8 text-center text-gray-500">
              No franchise inquiries found
            </Card>
          ) : (
            inquiries.map((inquiry) => (
              <Card key={inquiry.id} className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{inquiry.name}</h3>
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(inquiry.status)}
                        <span className="text-sm font-medium capitalize">{inquiry.status}</span>
                      </div>
                    </div>
                    {inquiry.businessName && (
                      <p className="text-sm text-gray-600 mb-2">
                        Business: <span className="font-medium">{inquiry.businessName}</span>
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(inquiry.created_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2 text-sm">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <a href={`mailto:${inquiry.email}`} className="text-blue-600 hover:underline">
                      {inquiry.email}
                    </a>
                  </div>
                  <div className="flex items-center space-x-2 text-sm">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <a href={`tel:${inquiry.phone}`} className="text-blue-600 hover:underline">
                      {inquiry.phone}
                    </a>
                  </div>
                  {inquiry.city && inquiry.state && (
                    <div className="flex items-center space-x-2 text-sm">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-700">
                        {inquiry.city}, {inquiry.state} {inquiry.zip}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-sm">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-700 font-medium">{inquiry.marketInterested}</span>
                  </div>
                </div>

                {inquiry.experience && (
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-gray-700 mb-1">Experience:</div>
                    <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      {inquiry.experience}
                    </div>
                  </div>
                )}

                {inquiry.message && (
                  <div className="mb-4">
                    <div className="text-sm font-semibold text-gray-700 mb-1">Message:</div>
                    <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      {inquiry.message}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(inquiry.id, "contacted")}
                    disabled={inquiry.status === "contacted"}
                  >
                    Mark Contacted
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateStatus(inquiry.id, "qualified")}
                    disabled={inquiry.status === "qualified"}
                  >
                    Mark Qualified
                  </Button>
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => updateStatus(inquiry.id, "approved")}
                    disabled={inquiry.status === "approved"}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => updateStatus(inquiry.id, "rejected")}
                    disabled={inquiry.status === "rejected"}
                  >
                    Reject
                  </Button>
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FranchiseInquiryManager;
